import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import moment from "moment";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { StoreExtendedService } from "../../shared/store-extended.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "../../auth/auth.service";
import { User } from "../../users/users.interface";
import { HttpClient } from "@angular/common/http";
import { MatDialog } from "@angular/material/dialog";
import { NotifyAdminDialogComponent } from "../../shared/notify-admin-dialog/notify-admin-dialog.component";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";
import { SyncRequiredDialogComponent } from "../../shared/sync-required-dialog/sync-required-dialog.component";
import { SyncRequiredDialogData } from "../../shared/sync-required-dialog/sync-required-dialog.interface";
import { isFeatureBlocked } from "../../shared/utils/plan-restriction.util";
import { SyncSuccessDialogComponent } from "../sync-success-dialog/sync-success-dialog.component";
import { MY_FORMATS } from "../../shared/shared.module";
import {
  GetIntegrationSettingsAction,
  ChangeIntegrationSettingsAction,
  CHANGE_INTEGRATION_SETTINGS_SUCCESS,
  CHANGE_INTEGRATION_SETTINGS_FAILURE,
} from "../../settings/settings.actions";
import { IntegrationSettings } from "../../settings/settings.interface";
import { getSettingsSelector } from "../../settings/settings.reducer";
import { IntegrationSettingsService } from "../integration-settings.service";
import { BillingSettings } from "../../settings/settings.interface";
import { environment } from "../../../environments/environment";
import { getCurrentAccount } from "../../accounts/accounts.reducer";
import {
  SyncMicrosoftEmployeesAction,
  SYNC_MICROSOFT_EMPLOYEES_SUCCESS,
  SYNC_MICROSOFT_EMPLOYEES_FAILURE,
} from "../../employees/employees.actions";
import { EmployeesService } from "../../employees/employees.service";

@Component({
  selector: "app-microsoft-integration",
  templateUrl: "./microsoft-integration.component.html",
  styleUrls: ["./microsoft-integration.component.scss"],
  standalone: false,
})
export class MicrosoftIntegrationComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() microsoftExpanded = true;
  @Input() otherAbsenceEnabled = false;
  @Input() billingSettings: BillingSettings | null = null;
  @Input() activeSyncs: {
    googleSyncStateId?: string;
    microsoftSyncStateId?: string;
    calamariSyncStateId?: string;
  } = {};
  @Output() microsoftExpandedChange = new EventEmitter<boolean>();
  @Output() absenceEnabledChange = new EventEmitter<boolean>();

  @ViewChild("proxyToggle") proxyToggle!: MatSlideToggle;

  public environment = environment;
  public currentDomain: string | null = null;
  public hasChanges = false;
  private initialSettings: {
    proxy: { active: boolean; activeAll: boolean; emails: string[] } | null;
    internalSignature: boolean;
    absenceEnabled: boolean;
    absenceLabel: string;
    absenceDaysAhead: number;
  } | null = null;

  public isSuperAdmin: boolean;
  public appUser: User | null | undefined = null;

  public readonly microsoftClientId = "4b56fadc-dead-40f5-8cbd-1a07ed63c623";
  public readonly microsoftScopes =
    "https://graph.microsoft.com/User.Read.All https://graph.microsoft.com/Group.Read.All";

  public lastMicrosoftSync: Date | undefined;
  public lastMicrosoftSyncFormatted: string | undefined;

  public proxyEnabled = false;
  public internalSignatureEnabled = false;
  public absenceEnabled = false;
  public absenceLabel = "";
  public absenceDaysAhead = 30;
  private currentSettings: IntegrationSettings | null = null;

  public savingProxy = false;
  private destroy$ = new Subject<void>();

  public microsoftStep1Completed = false;
  public microsoftStep2Completed = false;
  public microsoftStep3Completed = false;
  public microsoftStep4Completed = false;

  public spfCheckResult: boolean | null = null;

  public readonly proxyHostIp = "34.159.68.145";
  public readonly xGSignatureHeader = "X-gSignature-MessageProcessed";
  public readonly xGSignatureValue = "true";

  public microsoftSyncStateId: string | null = null;
  public showMicrosoftProgress = false;

  public isMicrosoftSyncInProgress = false;

  constructor(
    private readonly toastr: ToastrTranslatedService,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
    private readonly auth: AuthService,
    private readonly store: StoreExtendedService,
    private readonly actions$: ActionsSubject,
    private readonly integrationSettingsService: IntegrationSettingsService,
    private readonly http: HttpClient,
    private readonly employeesService: EmployeesService,
    private readonly dialog: MatDialog,
  ) {
    this.lastMicrosoftSync = this.auth.appUser.lastMicrosoftSync;
    if (this.lastMicrosoftSync) {
      this.lastMicrosoftSyncFormatted = moment(this.lastMicrosoftSync).format(
        MY_FORMATS.display.dateInput,
      );
    }
    this.isSuperAdmin = this.auth.isSuperAdmin;

    this.actions$
      .pipe(
        ofType(
          CHANGE_INTEGRATION_SETTINGS_SUCCESS,
          CHANGE_INTEGRATION_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.savingProxy = false;
      });
  }

  ngOnInit(): void {
    this.appUser = this.auth.appUser;

    this.store.select(getCurrentAccount).subscribe((domain) => {
      this.currentDomain = domain;
    });

    if (this.activeSyncs.microsoftSyncStateId) {
      this.microsoftSyncStateId = this.activeSyncs.microsoftSyncStateId;
      this.showMicrosoftProgress = true;
      this.isMicrosoftSyncInProgress = true;
    }

    this.store.dispatch(new GetIntegrationSettingsAction());
    this.store
      .select(getSettingsSelector)
      .subscribe((settings: IntegrationSettings | null) => {
        if (!settings) {
          return;
        }
        this.currentSettings = settings;
        this.proxyEnabled = settings.proxy?.active;
        this.internalSignatureEnabled = settings.internalSignature ?? false;
        this.absenceEnabled = settings.absenceEnabled ?? false;
        this.absenceLabel = settings.absenceLabel ?? "";
        this.absenceDaysAhead = settings.absenceDaysAhead ?? 30;

        if (this.proxyEnabled) {
          this.microsoftStep1Completed = true;
          this.microsoftStep2Completed = true;
          this.microsoftStep3Completed = true;
        }

        this.initialSettings = {
          proxy: settings.proxy
            ? {
                active: settings.proxy.active,
                activeAll: settings.proxy.activeAll,
                emails: settings.proxy.emails,
              }
            : null,
          internalSignature: this.internalSignatureEnabled,
          absenceEnabled: this.absenceEnabled,
          absenceLabel: this.absenceLabel,
          absenceDaysAhead: this.absenceDaysAhead,
        };
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showFreemiumRestrictionDialog = (plan: "free" | "freemium" | "basic") => {
    this.dialog.open(LimitPlanDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data: { plan, type: "general" },
    });
  };

  showSyncRequiredDialog = (
    provider: "GOOGLE" | "MICROSOFT" | "CALAMARI",
    onSync: () => void,
  ) => {
    const data: SyncRequiredDialogData = {
      provider,
      onSync,
    };

    this.dialog.open(SyncRequiredDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data,
    });
  };

  toggleProxy(checked: boolean): void {
    if (checked && isFeatureBlocked(this.isSuperAdmin, this.billingSettings)) {
      this.proxyToggle.checked = false;
      const planName = this.billingSettings?.planName?.toLowerCase() || "basic";
      this.showFreemiumRestrictionDialog(
        planName as "free" | "freemium" | "basic",
      );
      return;
    }

    if (checked && !this.lastMicrosoftSync) {
      this.proxyToggle.checked = false;
      this.showSyncRequiredDialog("MICROSOFT", () => this.syncMicrosoft());
      return;
    }

    this.proxyEnabled = checked;
    this.checkForChanges();
  }

  public onStepChange(): void {
    this.hasChanges = true;
  }

  onProxyToggleChange(): void {
    this.checkForChanges();
  }

  onInternalSignatureToggleChange(): void {
    this.checkForChanges();
  }

  get canDisableProxy(): boolean {
    return true;
  }

  onAbsenceLabelChange(): void {
    this.checkForChanges();
  }

  onAbsenceDaysAheadChange(): void {
    this.checkForChanges();
  }

  private checkForChanges(): void {
    if (!this.initialSettings) {
      return;
    }
    const currentProxy = this.proxyEnabled
      ? {
          active: this.proxyEnabled,
          activeAll: this.initialSettings.proxy?.activeAll ?? false,
          emails: this.initialSettings.proxy?.emails ?? [],
        }
      : {
          active: false,
          activeAll: false,
          emails: [],
        };
    const proxyChanged =
      JSON.stringify(currentProxy) !==
      JSON.stringify(this.initialSettings.proxy);

    this.hasChanges =
      proxyChanged ||
      this.internalSignatureEnabled !==
        this.initialSettings.internalSignature ||
      this.absenceEnabled !== this.initialSettings.absenceEnabled ||
      this.absenceLabel !== this.initialSettings.absenceLabel ||
      this.absenceDaysAhead !== this.initialSettings.absenceDaysAhead;
  }

  public onMicrosoftStepChange(): void {
    this.hasChanges = true;
  }

  public saveIntegrationSettings(): void {
    if (!this.currentSettings) {
      this.toastr.error(
        "settings.integration.microsoft.serverSide.currentSettings",
      );
      return;
    }
    this.savingProxy = true;
    const updatedSettings: IntegrationSettings = {
      ...this.currentSettings,
      proxy: this.proxyEnabled
        ? {
            active: this.proxyEnabled,
            activeAll: true,
            emails: this.initialSettings.proxy?.emails ?? [],
          }
        : {
            active: false,
            activeAll: false,
            emails: [],
          },
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel || null,
      absenceDaysAhead: this.absenceDaysAhead,
    };
    this.store
      .dispatchAndWaitForResult(
        new ChangeIntegrationSettingsAction(updatedSettings),
      )
      .subscribe(() => {
        this.store.dispatch(new GetIntegrationSettingsAction());
      });
    this.hasChanges = false;
    this.initialSettings = {
      proxy: updatedSettings.proxy
        ? {
            active: updatedSettings.proxy.active,
            activeAll: updatedSettings.proxy.activeAll,
            emails: updatedSettings.proxy.emails,
          }
        : null,
      internalSignature: this.internalSignatureEnabled,
      absenceEnabled: this.absenceEnabled,
      absenceLabel: this.absenceLabel?.trim() || "",
      absenceDaysAhead: this.absenceDaysAhead,
    };
  }

  public authorizeMicrosoft(): void {
    this.http.get("/microsoft/authorize").subscribe({
      next: (response: unknown) => {
        const res = response as { adminConsentUrl?: string };
        if (res.adminConsentUrl) {
          window.location.href = res.adminConsentUrl;
        }
      },
      error: () => {
        this.toastr.error(
          this.translate.instant(
            "settings.integration.microsoft.authUrlError",
          ) as string,
        );
      },
    });
  }

  syncMicrosoft() {
    if (this.isMicrosoftSyncInProgress) {
      return;
    }
    this.isMicrosoftSyncInProgress = true;
    this.showMicrosoftProgress = true;
    this.microsoftSyncStateId = null;
    this.employeesService.checkMicrosoftSyncLock().subscribe(
      (response) => {
        if (response && response.lock) {
          this.isMicrosoftSyncInProgress = false;
          this.showMicrosoftProgress = false;
          const message = this.translate.instant(
            "employee.syncAlreadyInProgress",
            {
              currentSyncUser: response.currentSyncUser,
            },
          ) as string;
          this.toastrService.info(message);
        } else {
          this.startMicrosoftSync();
        }
      },
      () => {
        this.isMicrosoftSyncInProgress = false;
        this.showMicrosoftProgress = false;
        this.toastr.error(
          this.translate.instant("employee.syncLockCheckError") as string,
        );
      },
    );
  }

  private startMicrosoftSync(): void {
    this.store.dispatch(new SyncMicrosoftEmployeesAction());

    this.actions$
      .pipe(
        ofType(
          SYNC_MICROSOFT_EMPLOYEES_SUCCESS,
          SYNC_MICROSOFT_EMPLOYEES_FAILURE,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((action: { payload?: { microsoftSyncStateId?: string } }) => {
        if (action.payload?.microsoftSyncStateId) {
          this.microsoftSyncStateId = action.payload.microsoftSyncStateId;
        }
      });
  }

  closeMicrosoftProgress(): void {
    this.isMicrosoftSyncInProgress = false;
    this.showMicrosoftProgress = false;
    this.microsoftSyncStateId = null;
  }

  onMicrosoftSyncCompleted(event: { success: boolean }): void {
    this.isMicrosoftSyncInProgress = false;
    if (!event.success) {
      return;
    }
    this.updateLastMicrosoftSync();
    void this.auth.fetchCurrent().then(() => {
      const synced = this.auth.appUser?.lastMicrosoftSync;
      if (!synced) {
        return;
      }
      this.lastMicrosoftSync = new Date(synced);
      this.lastMicrosoftSyncFormatted = moment(this.lastMicrosoftSync).format(
        MY_FORMATS.display.dateInput,
      );
    });
    this.showSyncSuccessDialog();
  }

  private updateLastMicrosoftSync() {
    this.lastMicrosoftSync = new Date();
    this.lastMicrosoftSyncFormatted = moment(this.lastMicrosoftSync).format(
      MY_FORMATS.display.dateInput,
    );
    if (this.auth.appUser) {
      this.auth.appUser.lastMicrosoftSync = this.lastMicrosoftSync;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeSyncs && changes.activeSyncs.currentValue) {
      const activeSyncs = changes.activeSyncs.currentValue as {
        microsoftSyncStateId?: string;
      };
      if (activeSyncs.microsoftSyncStateId) {
        this.microsoftSyncStateId = activeSyncs.microsoftSyncStateId;
        this.showMicrosoftProgress = true;
        this.isMicrosoftSyncInProgress = true;
      }
    }
  }

  toggleAbsence(checked: boolean): void {
    if (checked && this.otherAbsenceEnabled) {
      return;
    }

    this.absenceEnabled = checked;
    this.absenceEnabledChange.emit(this.absenceEnabled);
    this.checkForChanges();
  }

  toggleInternalSignature(checked: boolean): void {
    this.internalSignatureEnabled = checked;
    this.checkForChanges();
  }

  get isAllStepsCompleted(): boolean {
    return (
      !this.proxyEnabled ||
      (this.microsoftStep1Completed &&
        this.microsoftStep2Completed &&
        this.microsoftStep3Completed)
    );
  }

  get canSaveServerSide(): boolean {
    if (this.savingProxy || !this.hasChanges) {
      return false;
    }
    if (!this.proxyEnabled) {
      return true;
    }
    if (this.isAllStepsCompleted) {
      return true;
    }
    return this.initialSettings?.proxy?.active;
  }

  public copyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.toastr.success(
          this.translate.instant("copyToClipboard") as string,
        );
      })
      .catch(() => {
        this.toastr.error(
          this.translate.instant("copyToClipboardError") as string,
        );
      });
  }

  public verifySpfRecord(): void {
    this.spfCheckResult = null;
    this.integrationSettingsService.checkSpfRecord().subscribe({
      next: (res) => {
        this.spfCheckResult = res.valid;
        this.microsoftStep3Completed = res.valid;
        if (res.valid) {
          this.toastr.success(
            this.translate.instant(
              "settings.integration.microsoft.serverSide.spfValid",
            ) as string,
          );
        } else {
          this.toastr.error(
            this.translate.instant(
              "settings.integration.microsoft.serverSide.spfInvalid",
            ) as string,
          );
        }
      },
      error: () => {
        this.spfCheckResult = false;
        this.microsoftStep3Completed = false;
        this.toastr.error(
          this.translate.instant(
            "settings.integration.microsoft.serverSide.spfError",
          ) as string,
        );
      },
    });
  }

  private showSyncSuccessDialog(): void {
    this.dialog.open(SyncSuccessDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }

  openNotifyAdminDialog(): void {
    this.dialog.open(NotifyAdminDialogComponent, {
      width: "700px",
      maxWidth: "90vw",
      data: { provider: "microsoft" },
    });
  }
}
